export const COPILOT_TOOL_DATA_WAIT_MAX_MS = 10_000
export const COPILOT_TOOL_DATA_WAIT_INITIAL_DELAY_MS = 300
export const COPILOT_TOOL_DATA_WAIT_MAX_DELAY_MS = 2_000
export const COPILOT_TOOL_DATA_WAIT_BACKOFF_FACTOR = 2

export interface CopilotToolDataWaitMetadata {
  waitedMs: number
  attempts: number
  timedOut: boolean
}

/** When present on a tool result, signals the handler wrapper to wait while false. */
export interface CopilotToolDataResult {
  isDataLoaded?: boolean
  _dataWait?: CopilotToolDataWaitMetadata
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value != null && !Array.isArray(value)

/**
 * Returns whether a tool result signals that page data is still loading.
 * Matches results that explicitly set `isDataLoaded` to false.
 */
export const isDataLoadingResult = (result: unknown): boolean =>
  isRecord(result) && 'isDataLoaded' in result && result.isDataLoaded === false

/**
 * Attaches retry metadata to a tool result after waiting for data to load.
 * No-op when the result is not a plain object.
 */
const attachDataWaitMetadata = <T>(result: T, metadata: CopilotToolDataWaitMetadata): T => {
  if (!isRecord(result)) {
    return result
  }

  return {
    ...result,
    _dataWait: metadata
  }
}

export interface WaitForDataLoadedOptions {
  maxWaitMs?: number
  initialDelayMs?: number
  maxDelayMs?: number
  backoffFactor?: number
}

const sleep = async (ms: number): Promise<void> => {
  await new Promise((resolve) => setTimeout(resolve, ms))
}

/**
 * Retries a tool handler with exponential backoff until data is loaded or the wait budget expires.
 *
 * Re-executes the handler while results include `isDataLoaded: false`, then attaches
 * `_dataWait` metadata when at least one retry occurred.
 */
export const waitForDataLoaded = async <T>(
  execute: () => T | Promise<T>,
  options: WaitForDataLoadedOptions = {}
): Promise<T> => {
  const maxWaitMs = options.maxWaitMs ?? COPILOT_TOOL_DATA_WAIT_MAX_MS
  const maxDelayMs = options.maxDelayMs ?? COPILOT_TOOL_DATA_WAIT_MAX_DELAY_MS
  const backoffFactor = options.backoffFactor ?? COPILOT_TOOL_DATA_WAIT_BACKOFF_FACTOR
  let delayMs = options.initialDelayMs ?? COPILOT_TOOL_DATA_WAIT_INITIAL_DELAY_MS
  let waitedMs = 0
  let attempts = 0
  let lastResult = await execute()

  while (isDataLoadingResult(lastResult)) {
    if (waitedMs >= maxWaitMs) {
      break
    }

    const remainingMs = maxWaitMs - waitedMs
    const sleepMs = Math.min(delayMs, remainingMs)
    await sleep(sleepMs)
    waitedMs += sleepMs
    attempts += 1
    delayMs = Math.min(delayMs * backoffFactor, maxDelayMs)
    lastResult = await execute()
  }

  if (attempts === 0) {
    return lastResult
  }

  return attachDataWaitMetadata(lastResult, {
    waitedMs,
    attempts,
    timedOut: isDataLoadingResult(lastResult)
  })
}
